import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || '';

/**
 * Parse standardized API error response and return user-facing message.
 * Expects JSON like: { status, error, message }.
 * @param {string} responseText - Raw response text from API
 * @param {string} fallback - Message when parsing fails or no message/error field
 * @returns {string}
 */
function getApiErrorMessage(responseText, fallback = 'Something went wrong') {
  if (!responseText || typeof responseText !== 'string') return fallback;
  const trimmed = responseText.trim();
  if (!trimmed) return fallback;
  try {
    const json = JSON.parse(trimmed);
    if (json && typeof json.message === 'string' && json.message) return json.message;
    if (json && typeof json.error === 'string' && json.error) return json.error;
  } catch (_) {
    return trimmed.length <= 200 ? trimmed : fallback;
  }
  return fallback;
}

/** Status values for TPI requests */
export const TPI_REQUEST_STATUS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
};

class RequestsService {
  /**
   * Fetch all TPI requests raised by users.
   * GET /api/v1/get-all-tpi-requests
   * @returns {Promise<Array>}
   */
  static async getAllTpiRequests() {
    const url = `${API_BASE_URL}/api/v1/get-all-tpi-requests`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(getApiErrorMessage(text, `Failed to fetch requests (${response.status})`));
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Close a TPI request.
   * POST /api/v1/close-tpi-request?requestTpiId={id}
   * @param {number} requestTpiId
   * @returns {Promise<{status: any, message: string}>}
   */
  static async closeTpiRequest(requestTpiId) {
    const url = `${API_BASE_URL}/api/v1/close-tpi-request?requestTpiId=${encodeURIComponent(requestTpiId)}`;
    const response = await fetchWithAuth(url, { method: 'POST' });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(getApiErrorMessage(text, `Failed to close request (${response.status})`));
    }
    try {
      return text ? JSON.parse(text) : {};
    } catch (_) {
      return {};
    }
  }
}

export default RequestsService;
